import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from 'react-i18next';

export const useDeleteConfirmation = () => {
  const { showConfirmation, showToast, showDeleteErrorDialog, showPermanentDeleteConfirmation } =
    useToast();
  const { t } = useTranslation();

  const confirmDelete = (
    itemName: string,
    itemType: 'user' | 'device' | 'association',
    useDeactivateMode: boolean = false,
    onConfirm: () => Promise<void> | void
  ) => {
    const getTitle = () => {
      switch (itemType) {
        case 'user':
          return useDeactivateMode
            ? t('common.deactivateUserTitle')
            : t('common.permanentDeleteUserTitle');
        case 'device':
          return useDeactivateMode
            ? t('common.deactivateDeviceTitle')
            : t('common.permanentDeleteDeviceTitle');
        case 'association':
          return useDeactivateMode
            ? t('common.deactivateAssociationTitle')
            : t('common.deleteAssociationTitle');
        default:
          return useDeactivateMode ? t('common.deactivateTitle') : t('common.deleteTitle');
      }
    };

    const getMessage = () => {
      switch (itemType) {
        case 'user':
          return useDeactivateMode
            ? t('common.deactivateUserMessage', { name: itemName })
            : t('common.permanentDeleteUserMessage', { name: itemName });
        case 'device':
          return useDeactivateMode
            ? t('common.deactivateDeviceMessage', { name: itemName })
            : t('common.permanentDeleteDeviceMessage', { name: itemName });
        case 'association':
          return useDeactivateMode
            ? t('common.deactivateAssociationMessage', { name: itemName })
            : t('common.deleteAssociationMessage', { name: itemName });
        default:
          return useDeactivateMode
            ? t('common.deactivateMessage', { name: itemName })
            : t('common.deleteMessage', { name: itemName });
      }
    };

    // Success message is now handled by the calling component
    // const getSuccessMessage = () => { ... };

    const getErrorMessage = () => {
      switch (itemType) {
        case 'user':
          return useDeactivateMode
            ? t('common.userDeactivateError', { name: itemName })
            : t('common.userPermanentDeleteError', { name: itemName });
        case 'device':
          return useDeactivateMode
            ? t('common.deviceDeactivateError', { name: itemName })
            : t('common.devicePermanentDeleteError', { name: itemName });
        case 'association':
          return t('common.associationDeleteError', { name: itemName });
        default:
          return useDeactivateMode
            ? t('common.userDeactivateError', { name: itemName })
            : t('common.deleteError', { name: itemName });
      }
    };

    // Use specialized dialog for permanent deletions
    if (!useDeactivateMode) {
      showPermanentDeleteConfirmation({
        title: getTitle(),
        message: getMessage(),
        warningText: t('common.permanentDeleteWarning'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel'),
        type: 'danger',
        onConfirm: async () => {
          try {
            await onConfirm();
            // Only show success toast if the operation was actually successful
            // The onConfirm function should handle its own success/error feedback
          } catch (error) {
            console.error('Error deleting item:', error);
            showToast(getErrorMessage(), 'error');
          }
        },
      });
    } else {
      // Use regular confirmation dialog for deactivations
      showConfirmation({
        title: getTitle(),
        message: getMessage(),
        confirmText: t('common.deactivate'),
        cancelText: t('common.cancel'),
        type: 'danger',
        onConfirm: async () => {
          try {
            await onConfirm();
            // Only show success toast if the operation was actually successful
            // The onConfirm function should handle its own success/error feedback
          } catch (error) {
            console.error('Error deactivating item:', error);
            showToast(getErrorMessage(), 'error');
          }
        },
      });
    }
  };

  const showErrorDialog = (
    itemName: string,
    itemType: 'user' | 'device' | 'association',
    errorCode: string
  ) => {
    const getErrorTitle = () => {
      switch (itemType) {
        case 'user':
          return t('common.userHasAssociationsTitle');
        case 'device':
          return t('common.deviceHasLecturesTitle');
        default:
          return t('common.error');
      }
    };

    const getErrorMessage = () => {
      switch (itemType) {
        case 'user':
          return t('common.userHasAssociationsMessage', { name: itemName });
        case 'device':
          if (errorCode === 'DEVICE_HAS_LECTURES') {
            return t('common.deviceHasLecturesMessage', { name: itemName });
          } else if (errorCode === 'DEVICE_HAS_ASSOCIATIONS') {
            return t('common.deviceHasAssociationsMessage', { name: itemName });
          }
          return t('common.deviceHasLecturesMessage', { name: itemName });
        default:
          return t('common.error');
      }
    };

    const getErrorReason = () => {
      switch (itemType) {
        case 'user':
          return t('common.userHasAssociationsReason');
        case 'device':
          if (errorCode === 'DEVICE_HAS_LECTURES') {
            return t('common.deviceHasLecturesReason');
          } else if (errorCode === 'DEVICE_HAS_ASSOCIATIONS') {
            return t('common.deviceHasAssociationsReason');
          }
          return t('common.deviceHasLecturesReason');
        default:
          return t('common.error');
      }
    };

    showDeleteErrorDialog({
      title: getErrorTitle(),
      message: getErrorMessage(),
      errorReason: getErrorReason(),
      confirmText: t('common.done'),
      type: 'warning',
      onConfirm: () => {
        // No action needed, just close the dialog
      },
    });
  };

  return { confirmDelete, showErrorDialog };
};
